import { motion } from "framer-motion";
import { BlueprintGrid, CrossMarker } from "../components/BlueprintGrid";
import { lineMask, EASE, fadeUp, viewportOnce } from "../lib/motion";
import { HERO_IMAGES } from "../lib/product";
import { LOCATION_COORDINATE_META } from "../lib/site";

const LINES = ["CONSTRUCTION SHOULD BE", "BUILT ON CLARITY,", "NOT GUESSWORK."];

const AboutHero = () => (
    <section
        data-testid="about-hero"
        className="relative overflow-hidden bg-ink"
    >
        <BlueprintGrid />
        <CrossMarker className="absolute right-10 top-28 hidden text-white/25 md:block" />

        <div className="relative z-10 mx-auto w-full max-w-[1600px] px-6 pb-16 pt-40 md:px-10 lg:px-16">
            <motion.p
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
                className="flex items-center gap-3 font-mono text-[13px] tracking-[0.3em] text-mist"
            >
                <span className="h-1.5 w-1.5 bg-accent" />
                WHO WE ARE
            </motion.p>

            <h1
                data-testid="about-hero-headline"
                className="mt-8 font-display text-[clamp(1.5rem,5vw,5rem)] font-bold leading-[1.08] text-white"
            >
                {LINES.map((line, i) => (
                    <span
                        key={line}
                        className="-mb-[0.08em] block overflow-hidden pb-[0.08em]"
                    >
                        <motion.span
                            className="block"
                            variants={lineMask}
                            initial="hidden"
                            animate="show"
                            custom={i}
                        >
                            {i === 1 ? (
                                <>
                                    BUILT ON{" "}
                                    <span className="text-accent">CLARITY,</span>
                                </>
                            ) : i === 2 ? (
                                <>
                                    NOT GUESSWORK
                                    <span className="text-accent">.</span>
                                </>
                            ) : (
                                line
                            )}
                        </motion.span>
                    </span>
                ))}
            </h1>

            <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease: EASE, delay: 0.7 }}
                className="mt-10 max-w-2xl text-lg font-light leading-relaxed text-mist md:text-2xl"
            >
                BuildEase is a South African residential construction
                intelligence platform designed to make the home-building
                journey more transparent, understandable and informed.
            </motion.p>
        </div>

        <div className="relative z-10 mx-auto grid max-w-[1600px] items-center gap-12 px-6 pb-20 md:px-10 lg:grid-cols-12 lg:px-16">
            <motion.div
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={viewportOnce}
                className="lg:col-span-7"
            >
                <p className="max-w-2xl text-[16px] leading-relaxed text-mist">
                    Building a home is one of the most significant financial
                    decisions a person can make. Yet, for many homeowners,
                    understanding architectural plans, estimating construction
                    costs, evaluating contractor quotations and navigating
                    building requirements can be complicated and overwhelming.
                </p>
                <p className="mt-6 max-w-2xl font-display text-xl font-medium leading-snug text-white md:text-2xl">
                    BuildEase exists to change that experience
                    <span className="text-accent">.</span>
                </p>
                <p className="mt-6 max-w-2xl text-[16px] leading-relaxed text-mist">
                    By bringing together artificial intelligence, construction
                    knowledge, cost intelligence and digital visualisation, we
                    are developing a platform that helps homeowners better
                    understand their projects and make more informed decisions.
                </p>
                <p className="mt-8 max-w-2xl border-l-2 border-accent pl-5 text-[16px] leading-relaxed text-white/90">
                    Our goal is simple: to give homeowners greater visibility,
                    understanding and confidence as they bring their homes to
                    life.
                </p>
            </motion.div>

            <motion.figure
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={viewportOnce}
                data-testid="about-hero-figure"
                className="group lg:col-span-5"
            >
                <div className="overflow-hidden border border-white/12">
                    <img
                        src={HERO_IMAGES.realistic}
                        alt="Contemporary South African home with a white plaster exterior — the kind of home BuildEase helps homeowners understand"
                        className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                    />
                </div>
                <figcaption className="mt-3 flex items-center justify-between font-mono text-[13px] tracking-[0.22em] text-cad">
                    <span>WHO WE ARE</span>
                    <span>RESIDENTIAL · SA</span>
                </figcaption>
            </motion.figure>
        </div>

        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.1 }}
            className="relative z-10 border-t border-white/10"
            data-testid="about-metadata-bar"
        >
            <div className="mx-auto flex max-w-[1600px] items-center justify-between gap-6 px-6 py-4 font-mono text-[13px] tracking-[0.24em] text-mist/80 md:px-10 lg:px-16">
                <span className="hidden sm:block">BUILD INTELLIGENCE</span>
                <span data-testid="about-stage-counter">ABOUT — WHO WE ARE</span>
                <span>SOUTH AFRICA</span>
                <span className="hidden md:block">{LOCATION_COORDINATE_META}</span>
            </div>
        </motion.div>
    </section>
);

export default AboutHero;
